import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGetCurrencies } from "../../../../../../api/services/program/programApis";
import {
  ProjectExpenseData,
  ProjectKeyDetailsResponse,
  ViewProjectFeeOrExpenseDetails,
} from "../../../../../../api/services/project/model";
import {
  useDeleteFeesAndExpense,
  useGetProjectFeesAndExpenses,
  useGetProjectFeesOrExpenseById,
} from "../../../../../../api/services/project/projectApis";
import {
  AccordionHeader,
  AccordionHeaderText,
  STATUS_BADGE_TYPE,
  WarningConfirmationModal,
} from "../../../../../../components";
import PermissionWrapper from "../../../../../../components/permissionWrapper/PermissionWrapper";
import { MODULE_PERMISSIONS } from "../../../../../../helpers/constants";
import {
  TableActionType,
  TableActions,
} from "../../../../../../helpers/types/common";
import {
  getValueOrBlankArray
} from "../../../../../../helpers/utils";
import { usePermissions } from "../../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../redux/slices/appState/appStateSlice";
import {
  keyDetailsSelector,
  projectStatusSelector,
} from "../../../../../../redux/slices/project/projectSelector";
import { useGetProjectFeeConfig } from "../../../../hooks/useProjectFee";
import { projectFeesAndExpensesSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { ProjectAddFeesAndExpense } from "./components/projectAddFeesAndExpense/ProjectAddFeesAndExpense";
import { ProjectFeesAndExpenseList } from "./components/ProjectFeesAndExpenseList";
import { ProjectFeesAndExpenseMainWrapper } from "./ProjectFeesAndExpenseMain.style";
interface ProjectFeesAndExpenseMainProps {
  isAddButtonVisible?: boolean;
  poNumber?: string;
  billingCurrency?: string;
  isViewOnly?: boolean;
  isRefetch?: boolean;
  isEditProject?: boolean;
}
export const ProjectFeesAndExpenseMain = ({
  isAddButtonVisible = true,
  billingCurrency: projectFeeTabBillingCurrency,
  poNumber,
  isViewOnly = false,
  isRefetch,
  isEditProject,
}: ProjectFeesAndExpenseMainProps) => {
  const { useFetchLabel, commonLabels } = useSiteCore();
  const {
    id: projectId,
    client,
    clientType,
  } = useSelector(keyDetailsSelector) || ({} as ProjectKeyDetailsResponse);

  const { id: clientId } = client || {};

  const projectStatus = useSelector(projectStatusSelector);

  const getLabel = useFetchLabel(
    projectFeesAndExpensesSiteCoreKeys.feesAndExpenses,
  );
  const {
    projectFeeBillingCurrency,
    isError: isGetProjectFeeConfigError,
    isLoading: isProjectFeeConfigLoading,
    fetchProjectFeeDetails,
  } = useGetProjectFeeConfig();

  const { data: expenseList, mutateAsync: getProjectFeesAndExpenses } =
    useGetProjectFeesAndExpenses(); // Need to update this
  const { hasModulePermission } = usePermissions();

  const {
    mutateAsync: deleteFeesAndExpense,
    isPending: isDeleteFeesAndExpensePending,
    isError: isDeleteFeesAndExpenseError,
    isSuccess: isDeleteFeesAndExpenseSuccess,
  } = useDeleteFeesAndExpense();

  const [state, setState] = useState<{
    selectedExpenseDetails:
      | ViewProjectFeeOrExpenseDetails
      | ProjectExpenseData
      | null;
    showDeleteFeesAndExpenseModal: boolean;
    showAddFeesAndExpenseModal: boolean;
    feesExpenseList: ProjectExpenseData[];
  }>({
    showDeleteFeesAndExpenseModal: false,
    showAddFeesAndExpenseModal: false,
    selectedExpenseDetails: null,
    feesExpenseList: [],
  });

  const dispatch = useDispatch();

  const {
    data: expenseData,
    isPending: isGettingExpense,
    isSuccess: isGetExpenseSuccess,
    isError: isGetExpenseError,
    fetchById: getExpenseData,
  } = useGetProjectFeesOrExpenseById();

  const { data: currencyList, isPending: isGettingCurrencyList } =
    useGetCurrencies();

  const billingCurrencyName = useMemo(() => {
    if (projectFeeTabBillingCurrency) {
      return (
        currencyList?.find(
          (currency) => currency.id === projectFeeTabBillingCurrency,
        )?.name ?? ""
      );
    } else if (projectFeeBillingCurrency) {
      return projectFeeBillingCurrency.name;
    }
    return "";
  }, [projectFeeTabBillingCurrency, projectFeeBillingCurrency, currencyList]);

  const handleAddFeesAndExpense = () => {
    setState((prevState) => ({
      ...prevState,
      showAddFeesAndExpenseModal: true,
      selectedExpenseDetails: null,
    }));
  };
  const billingCurrencyId = useMemo(() => {
    if (projectFeeTabBillingCurrency) {
      return projectFeeTabBillingCurrency;
    } else if (projectFeeBillingCurrency) {
      return projectFeeBillingCurrency.id;
    }
    return "";
  }, [projectFeeTabBillingCurrency, projectFeeBillingCurrency]);

  const deleteFeesAndExpenseConfirmationText = getLabel(
    projectFeesAndExpensesSiteCoreKeys.deleteFeesAndExpenseContentText,
  );

  useEffect(() => {
    if (isRefetch && projectId) getProjectFeesAndExpenses(projectId);
  }, [isRefetch, projectId]);

  useEffect(() => {
    if (projectId && clientId && !projectFeeTabBillingCurrency) {
      fetchProjectFeeDetails(projectId);
    }
  }, [projectId, clientId, projectFeeTabBillingCurrency]);

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      feesExpenseList: getValueOrBlankArray(expenseList),
    }));
  }, [expenseList]);
  useEffect(() => {
    if (
      isGettingExpense ||
      isGettingCurrencyList ||
      isProjectFeeConfigLoading ||
      isDeleteFeesAndExpensePending
    ) {
      dispatch(
        toggleLoader({
          isVisible: true,
        }),
      );
    } else {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    isGettingExpense,
    isProjectFeeConfigLoading,
    isGettingCurrencyList,
    isDeleteFeesAndExpensePending,
  ]);

  useEffect(() => {
    if (
      isGetExpenseError ||
      isGetProjectFeeConfigError ||
      isDeleteFeesAndExpenseError
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [
    isGetExpenseError,
    isGetProjectFeeConfigError,
    isDeleteFeesAndExpenseError,
  ]);

  useEffect(() => {
    if (isGetExpenseSuccess && expenseData) {
      setState((prevState) => ({
        ...prevState,
        selectedExpenseDetails: expenseData,
        showAddFeesAndExpenseModal: true,
      }));
    }
  }, [isGetExpenseSuccess, expenseData]);

  useEffect(() => {
    if (isDeleteFeesAndExpenseSuccess) {
      const expenses = [...state.feesExpenseList];
      const index = expenses.findIndex(
        (expense) =>
          expense.id ===
          (state.selectedExpenseDetails as ProjectExpenseData).id,
      );
      if (index !== -1) {
        expenses.splice(index, 1);
      }
      setState((prevState) => ({
        ...prevState,
        showDeleteFeesAndExpenseModal: false,
        feesExpenseList: expenses,
      }));
    }
  }, [isDeleteFeesAndExpenseSuccess]);

  const handleActionClick = (
    selectedFeesAndExpense: ProjectExpenseData,
    actionName: TableActionType,
  ) => {
    if (actionName === TableActions.edit) {
      getExpenseData(selectedFeesAndExpense.id);
    } else if (actionName === TableActions.delete) {
      setState((prevState) => ({
        ...prevState,
        showDeleteFeesAndExpenseModal: true,
        selectedExpenseDetails: selectedFeesAndExpense,
      }));
    }
  };
  const onHideAddFeesAndExpenseModal = (isReloadList?: boolean) => {
    if (isReloadList) {
      getProjectFeesAndExpenses(projectId);
    }
    setState((prevState) => ({
      ...prevState,
      showAddFeesAndExpenseModal: false,
    }));
  };
  const handleDeleteFeesAndExpenseModal = () => {
    setState((prevState) => ({
      ...prevState,
      showDeleteFeesAndExpenseModal: false,
      selectedExpenseDetails: null,
    }));
  };

  const handleConfirmFeesAndExpenseDelete = async () => {
    deleteFeesAndExpense(
      (state.selectedExpenseDetails as ProjectExpenseData).id,
    );
  };

  const hasAddFeesAndExpensePermission = hasModulePermission(
    MODULE_PERMISSIONS.PROJECT_ADD_FEES_AND_EXPENSES,
  );

  const isProjectNotClosed = projectStatus !== STATUS_BADGE_TYPE.closed;

  const isAddFeesAndExpenseBtnVisible =
    isProjectNotClosed && hasAddFeesAndExpensePermission && isAddButtonVisible;

  return (
    <ProjectFeesAndExpenseMainWrapper>
      <PermissionWrapper
        permission={MODULE_PERMISSIONS.PROJECT_VIEW_FEES_AND_EXPENSES}
      >
        <AccordionHeader
          wrapperClassName="participant-accordion"
          title={
            <AccordionHeaderText
              label={getLabel(
                projectFeesAndExpensesSiteCoreKeys.billingItemsSection,
              )}
            />
          }
        >
          <ProjectFeesAndExpenseList
            isAddButtonVisible={isAddFeesAndExpenseBtnVisible}
            handleAddFeesAndExpense={handleAddFeesAndExpense}
            onActionClick={handleActionClick}
            projectExpenseList={state.feesExpenseList}
            projectId={projectId}
            billingCurrencyName={billingCurrencyName}
            onRefetchApi={getProjectFeesAndExpenses}
            isViewOnly={hasAddFeesAndExpensePermission ? isViewOnly : true}
            poNumber={poNumber}
            isEditProject={isEditProject}
            clientType={clientType}
          />
        </AccordionHeader>
      </PermissionWrapper>
      {state.showAddFeesAndExpenseModal && (
        <ProjectAddFeesAndExpense
          onHideProjectAddFeesAndExpenseModal={onHideAddFeesAndExpenseModal}
          selectedExpenseDetails={
            state.selectedExpenseDetails as ViewProjectFeeOrExpenseDetails
          }
          showProjectAddFeesAndExpenseModal={state.showAddFeesAndExpenseModal}
          currencyList={currencyList!}
          billingCurrencyId={billingCurrencyId}
          billingCurrencyName={billingCurrencyName}
          projectId={projectId}
        />
      )}
      {state.showDeleteFeesAndExpenseModal && (
        <WarningConfirmationModal
          labelContent={deleteFeesAndExpenseConfirmationText}
          modalTitle={commonLabels.confirmation}
          show={true}
          onClose={handleDeleteFeesAndExpenseModal}
          onPrimaryButtonClick={handleConfirmFeesAndExpenseDelete}
          primaryButtonTitle={commonLabels.yes}
          secondaryButtonTitle={commonLabels.no}
        />
      )}
    </ProjectFeesAndExpenseMainWrapper>
  );
};
